import { useState, useEffect, useRef } from 'react';

import { EVENTS } from '@constants/telemetryEvents';
import { Button } from '@components/common/Button';
import { useTelemetry } from '@hooks/useTelemetry';
import { Modal } from '@components/common/Modal';
import { TextField } from '@components/common/TextField';
import { ThursdayDatePicker } from '@components/common/ThursdayDatePicker';
import { Icon } from '@components/Icon';
import { apiFetchUrl, assertApiConfigured } from '@constants/apiConfig';
import { getSelectableThursdaysFromToday } from '@constants/semester';
import {
  canSubmitScheduleVisit,
  validateScheduleVisitForm,
} from '@utils';

import './ScheduleVisitModal.css';

export interface ScheduleVisitModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Thursdays (YYYY-MM-DD) for the date picker from the active semester.
   * Empty or omitted → visit date is optional (director will arrange by email).
   */
  thursdays?: string[];
}

const INTRO_WITH_DATES =
  "We'd love to meet you! Pick a Thursday and we'll line someone up to show you around.";

const INTRO_NO_DATES =
  "We'd love to meet you! Leave your details and we'll be in touch to arrange a visit.";

type ScheduleTouched = { name: boolean; email: boolean; phone: boolean; visitDate: boolean };

const INITIAL_SCHEDULE_TOUCHED: ScheduleTouched = {
  name: false,
  email: false,
  phone: false,
  visitDate: false,
};

/** Honeypot: hidden from users; bots fill it. */
const HONEYPOT_FIELD_NAME = 'hp_leave_empty';

export function ScheduleVisitModal({ isOpen, onClose, thursdays: thursdaysProp }: ScheduleVisitModalProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const { track } = useTelemetry();
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [touched, setTouched] = useState<ScheduleTouched>(INITIAL_SCHEDULE_TOUCHED);
  const [focused, setFocused] = useState<ScheduleTouched>(INITIAL_SCHEDULE_TOUCHED);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const thursdays = thursdaysProp ?? [];
  const selectableThursdays = getSelectableThursdaysFromToday(thursdays);
  const requireVisitDate = selectableThursdays.length > 0;

  const setFieldTouched = (field: keyof ScheduleTouched) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    setFocused((prev) => ({ ...prev, [field]: false }));
  };

  const setFieldFocused = (field: keyof ScheduleTouched, value: boolean) => {
    setFocused((prev) => ({ ...prev, [field]: value }));
  };

  /** Reset the form when the modal closes (adjust state during render). */
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setStatus('idle');
      setTouched(INITIAL_SCHEDULE_TOUCHED);
      setFocused(INITIAL_SCHEDULE_TOUCHED);
      setName('');
      setEmail('');
      setPhone('');
      setVisitDate('');
    }
  }

  /** Clear visitDate if it's no longer selectable (e.g. was a past Thursday). */
  if (
    visitDate &&
    selectableThursdays.length > 0 &&
    !selectableThursdays.includes(visitDate)
  ) {
    setVisitDate('');
  }

  /** Sync autofilled/pasted values into state (browser autofill and some paste don't fire onChange). */
  const syncFormFromDOM = () => {
    const form = formRef.current;
    if (!form) return;
    const nameEl = form.elements.namedItem('name') as HTMLInputElement | null;
    const emailEl = form.elements.namedItem('email') as HTMLInputElement | null;
    const phoneEl = form.elements.namedItem('phone') as HTMLInputElement | null;
    const visitDateEl = form.elements.namedItem('visitDate') as HTMLInputElement | null;
    if (nameEl?.value) setName((prev) => (prev !== nameEl.value ? nameEl.value : prev));
    if (emailEl?.value) setEmail((prev) => (prev !== emailEl.value ? emailEl.value : prev));
    if (phoneEl?.value) setPhone((prev) => (prev !== phoneEl.value ? phoneEl.value : prev));
    if (visitDateEl?.value) setVisitDate((prev) => (prev !== visitDateEl.value ? visitDateEl.value : prev));
  };

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(syncFormFromDOM, 300);
    return () => clearTimeout(t);
  }, [isOpen]);

  const handleFormInput = () => {
    syncFormFromDOM();
  };

  const formFields = {
    name,
    email,
    phone,
    visitDate,
    requireVisitDate,
  };
  const scheduleValidation = validateScheduleVisitForm(formFields);
  const canSubmit = canSubmitScheduleVisit(formFields);

  async function submitScheduleVisitForm(honeypotValue: string): Promise<void> {
    assertApiConfigured();
    const response = await fetch(apiFetchUrl('/schedule-visit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        email,
        phone,
        visitDate: visitDate.trim() || undefined,
        honeypot: honeypotValue || undefined,
      }),
    });
    if (!response.ok) {
      const err: unknown = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: { message?: string } })?.error?.message ??
          `Request failed (${response.status})`
      );
    }
  }

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotValue =
      (form.elements.namedItem(HONEYPOT_FIELD_NAME) as HTMLInputElement)?.value ?? '';
    if (typeof honeypotValue === 'string' && honeypotValue.trim().length > 0) return;
    if (!canSubmit) {
      setTouched({ name: true, email: true, phone: true, visitDate: true });
      setFocused(INITIAL_SCHEDULE_TOUCHED);
      return;
    }
    setIsSubmitting(true);
    setStatus('idle');
    try {
      await submitScheduleVisitForm(honeypotValue);
      setStatus('success');
      track(EVENTS.LANDING_SCHEDULE_VISIT_SUBMITTED, {
        hasVisitDate: visitDate.trim() ? 'true' : 'false',
      });
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVisitDateChange = (value: string) => {
    setVisitDate(value);
    setFieldTouched('visitDate');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Come visit us!"
      description={requireVisitDate ? INTRO_WITH_DATES : INTRO_NO_DATES}
      closeButtonLabel="Close"
      headerContent={
        <Icon
          type="calendar"
          style="filled"
          colorVariant="blue"
          size="lg"
          aria-hidden
        />
      }
      maxWidth="420px"
    >
      {status === 'success' ? (
        <div className="bhg-schedule-visit__success">
          <p className="bhg-schedule-visit__success-text">
            Thanks! We&apos;ll be in touch soon to confirm your visit.
          </p>
          <Button variant="primary" size="md" onClick={onClose}>
            Done
          </Button>
        </div>
      ) : (
        <form
          ref={formRef}
          className="bhg-schedule-visit__form"
          onSubmit={handleSubmit}
          onInput={handleFormInput}
          noValidate
        >
          <TextField
            label="Your name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={() => setFieldFocused('name', true)}
            onBlur={() => setFieldTouched('name')}
            required
            // First field of a just-opened dialog: moving focus here is the expected dialog behavior.
            // eslint-disable-next-line jsx-a11y-x/no-autofocus -- initial focus inside modal
            autoFocus
            autoComplete="name"
            fullWidth
            error={touched.name && !focused.name ? scheduleValidation.errors.name : undefined}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFieldFocused('email', true)}
            onBlur={() => setFieldTouched('email')}
            required
            autoComplete="email"
            fullWidth
            error={touched.email && !focused.email ? scheduleValidation.errors.email : undefined}
          />
          <TextField
            label="Phone"
            name="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onFocus={() => setFieldFocused('phone', true)}
            onBlur={() => setFieldTouched('phone')}
            required
            autoComplete="tel"
            fullWidth
            error={touched.phone && !focused.phone ? scheduleValidation.errors.phone : undefined}
          />
          {requireVisitDate ? (
            <div>
              <ThursdayDatePicker
                label="Which Thursday?"
                name="visitDate"
                value={visitDate}
                onChange={handleVisitDateChange}
                onFocus={() => setFieldFocused('visitDate', true)}
                onBlur={() => setFieldTouched('visitDate')}
                selectableDates={selectableThursdays}
                required
                fullWidth
              />
              {touched.visitDate && !focused.visitDate && scheduleValidation.errors.visitDate && (
                <p className="bhg-schedule-visit__field-error" role="alert">
                  {scheduleValidation.errors.visitDate}
                </p>
              )}
            </div>
          ) : (
            <p className="bhg-schedule-visit__no-dates" role="note">
              Semester dates are not posted yet — we&apos;ll email you to pick a time that works.
            </p>
          )}
          <div className="bhg-schedule-visit__honeypot" aria-hidden="true">
            <label>
              Leave this blank
              <input
                type="text"
                name={HONEYPOT_FIELD_NAME}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden
              />
            </label>
          </div>
          {status === 'error' && (
            <p className="bhg-schedule-visit__field-error" role="alert">
              Something went wrong. Please try again.
            </p>
          )}
          <div className="bhg-schedule-visit__actions">
            <Button type="button" variant="ghost" size="md" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={!canSubmit}
              loading={isSubmitting}
            >
              Request a tour
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
